// Matt: "it's harder for me to reload the page after you make changes...
// can we make it so [reopening the app] can also detect new changes and
// reload the page?" A standalone home-screen launch has no URL bar/reload
// button, and unlike a normal browser tab, iOS doesn't reliably re-fetch
// anything just because the app was brought back to the foreground - it
// can resume the exact same already-loaded page (and JS) indefinitely.
//
// Then, later: "All of our apps need the new version auto reload feature we
// have in Cinemaroll." What that added over the original version here is
// everything below the DETECTION section - more triggers than
// visibilitychange alone, and a real "is this a safe moment" gate instead of
// reloading the instant a new build is spotted. The safety half lives in
// appUpdate.js; read its header for what a reload actually costs in a game.
//
// --- DETECTION ---------------------------------------------------------
//
// Two independent signals, either of which means "a newer build is live."
// Deliberately NOT the service worker's own `updated()` lifecycle hook: this
// app builds with skipWaiting (vue.config.js), so a new worker activates
// itself immediately instead of sitting in `installed` where that hook could
// see it. Detection that depends on winning that race silently doesn't fire.
// Comparing what the server is serving against what this page actually
// loaded doesn't depend on any worker timing at all.
//
//  1. version.json vs VUE_APP_GIT_SHA. vue.config.js bakes the short SHA into
//     every build (for Landing.vue's version footer); scripts/writeVersion.mjs
//     writes the SAME value to public/version.json, which vue.config.js
//     explicitly excludes from the Workbox precache precisely so this fetch
//     reaches the network.
//
//  2. The hashed entry bundle - `js/app.<hash>.js` - in the deployed
//     index.html vs the one in this page's own <script> tags. This is the
//     signal Cinemaroll uses, and it's here because signal 1 has a real hole
//     in this repo: deploys are a local `yarn deploy` off a working tree, and
//     both writeVersion.mjs and vue.config.js stamp a dirty tree as
//     `<sha>-dirty`. Deploy twice from a dirty tree with different code and
//     the version string is identical both times; the bundle hash isn't.
//
// The `?updateCheck=` param on the index.html fetch matters: Workbox
// precaches index.html and only strips params it's told to, so an arbitrary
// one misses the precache entry and goes to the network - which is the point.
//
// No special-casing for local dev: `yarn serve` has a real VUE_APP_GIT_SHA
// (vue.config.js sets it unconditionally) but no public/version.json (only
// `build`/`deploy` run writeVersion.mjs) and no hashed bundle names, so both
// signals come back empty and the whole thing is a harmless no-op there.
import {
  isSafeMomentForReload, reloadForUpdate, shouldAutoAttempt,
} from './appUpdate';

const RELOAD_MESSAGE_MS = 1200;
// "Fresh" = the app was opened or foregrounded a moment ago, so nothing can
// be in flight yet. Reload straight away rather than making them wait.
const FRESH_MOMENT_MS = 5000;
// Otherwise: no pointer, key, wheel, touch or scroll event for this long.
const QUIET_STRETCH_MS = 25000;
const QUIET_POLL_MS = 5000;
// A backstop that doesn't depend on any lifecycle event firing at all.
const BACKGROUND_CHECK_MS = 30 * 60 * 1000;

const ENTRY_BUNDLE_PATTERN = /js\/app\.[a-z0-9]+\.js/;

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll'];

function baseUrl() {
  return process.env.BASE_URL || '/';
}

/** The entry bundle THIS page is running, read off its own script tags. */
export function currentBundleName(root = typeof document !== 'undefined' ? document : null) {
  const scripts = Array.from(root?.querySelectorAll?.('script[src]') || []);
  for (const script of scripts) {
    const match = (script.getAttribute('src') || '').match(ENTRY_BUNDLE_PATTERN);
    if (match) return match[0];
  }
  return null;
}

async function fetchDeployedVersion() {
  try {
    const res = await fetch(`${baseUrl()}version.json`, { cache: 'no-store' });
    if (!res.ok) return null;
    const data = await res.json();
    return data?.version || null;
  } catch {
    return null;
  }
}

async function fetchDeployedBundle() {
  try {
    const res = await fetch(`${baseUrl()}index.html?updateCheck=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    return (await res.text()).match(ENTRY_BUNDLE_PATTERN)?.[0] || null;
  } catch {
    return null;
  }
}

// Matt: "when new changes were discovered, it['d] give me a very brief
// message first. It just said something like new changes detected,
// reloading." Plain DOM injection (not a Vue component) since this needs to
// work regardless of which route/component tree happens to be mounted at the
// moment a new version is discovered.
function showReloadToast() {
  if (document.querySelector('.update-reload-toast')) return;
  const toast = document.createElement('div');
  toast.className = 'update-reload-toast';
  toast.textContent = 'New changes detected — reloading…';
  document.body.appendChild(toast);
}

/**
 * Wires up the update check. Pass the store and router so the safe-moment
 * gate can see whether a game is in progress and which screen we're on -
 * both optional, and without them the gate simply falls back to "on an
 * unknown screen with no game", which is the conservative reading.
 *
 * Returns a disposer (removes every listener and timer). main.js never calls
 * it - the app never "un-initializes" - but tests do, since jsdom's
 * `document`/`window` are shared and persistent across an entire test file:
 * without this, each test's own initUpdateCheck() call would stack another
 * set of listeners on top of every previous test's.
 */
export function initUpdateCheck({ store = null, router = null } = {}) {
  const runningVersion = process.env.VUE_APP_GIT_SHA;
  const runningBundle = currentBundleName();

  let lastActivityAt = Date.now();
  let lastBecameVisibleAt = Date.now();
  let quietTimer = null;
  let armedTarget = null;

  function noteActivity() {
    lastActivityAt = Date.now();
  }

  function safeMomentContext() {
    return {
      routePath: router?.currentRoute?.value?.path || '',
      gameState: store?.state?.gameState || null,
      tutorialActive: Boolean(store?.state?.tutorialActive),
    };
  }

  function applyUpdate() {
    showReloadToast();
    setTimeout(() => { reloadForUpdate(); }, RELOAD_MESSAGE_MS);
  }

  // Found a new build. Apply it at the first moment that's actually safe -
  // immediately if we're still in the opening seconds of a launch/resume,
  // otherwise once they've been still for a while AND aren't mid-game. On a
  // game screen that second condition can stay false for an entire session,
  // which is the intended outcome, not a failure: the poll just keeps
  // ticking cheaply until the game is over or they leave the board.
  function armAutoUpdate(target) {
    if (!shouldAutoAttempt(target)) return;

    if (Date.now() - lastBecameVisibleAt < FRESH_MOMENT_MS && isSafeMomentForReload(safeMomentContext())) {
      applyUpdate();
      return;
    }

    if (quietTimer) clearInterval(quietTimer);
    quietTimer = setInterval(() => {
      if (Date.now() - lastActivityAt <= QUIET_STRETCH_MS) return;
      if (!isSafeMomentForReload(safeMomentContext())) return;
      clearInterval(quietTimer);
      quietTimer = null;
      applyUpdate();
    }, QUIET_POLL_MS);
  }

  async function check() {
    if (armedTarget) return; // already found one and waiting for a safe moment
    const [deployedVersion, deployedBundle] = await Promise.all([
      fetchDeployedVersion(),
      fetchDeployedBundle(),
    ]);
    const versionChanged = Boolean(deployedVersion && runningVersion && deployedVersion !== runningVersion);
    const bundleChanged = Boolean(deployedBundle && runningBundle && deployedBundle !== runningBundle);
    if (!versionChanged && !bundleChanged) return;
    armedTarget = `${deployedVersion || '?'}|${deployedBundle || '?'}`;
    armAutoUpdate(armedTarget);
  }

  // visibilitychange alone is unreliable on iOS, particularly for a
  // home-screen-installed PWA - it's a long-standing WebKit quirk that it
  // sometimes just doesn't fire when the app is brought back to the
  // foreground (it varies by iOS version, which is why this can silently work
  // on one iPhone and never on another). pageshow and window focus are more
  // consistent there, and the interval is a backstop that doesn't depend on
  // any lifecycle event firing at all - between the four, a new deploy gets
  // picked up even if any single trigger never fires.
  function handleVisibilityChange() {
    if (document.visibilityState !== 'visible') return;
    lastBecameVisibleAt = Date.now();
    check();
  }

  function handlePageShow() {
    lastBecameVisibleAt = Date.now();
    check();
  }

  function handleFocus() {
    check();
  }

  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('pageshow', handlePageShow);
  window.addEventListener('focus', handleFocus);
  // Passive: the quiet-moment detector must never delay a scroll or a tap on
  // the board.
  ACTIVITY_EVENTS.forEach((eventName) => {
    window.addEventListener(eventName, noteActivity, { passive: true });
  });
  const backgroundTimer = setInterval(check, BACKGROUND_CHECK_MS);

  return () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('pageshow', handlePageShow);
    window.removeEventListener('focus', handleFocus);
    ACTIVITY_EVENTS.forEach((eventName) => {
      window.removeEventListener(eventName, noteActivity);
    });
    clearInterval(backgroundTimer);
    if (quietTimer) clearInterval(quietTimer);
    quietTimer = null;
  };
}
